<template>
    <section class="section registered-competitors-listing">
        <div class="container-fluid">
            <div class="columns">
                <div class="column">
                    <h1 class="title">{{ translate("admin_competitors.title") }}</h1>
                </div>
            </div>

            <RegisteredCompetitorsTopBar />

            <RegisteredCompetitorsTable />

            <RegisteredCompetitorsBottomBar />
        </div>

        <RegisteredCompetitorCopyToCategoryModal ref="copyToCategoryModal" />
        <RegisteredCompetitorDeleteModal ref="deleteCompetitorModal" />
        <EditRegisteredCompetitorModal ref="editRegisteredCompetitorModal" :event-id="store.eventId" />
        <PaymentsSettingsModal ref="paymentSettingsModal" :event-id="store.eventId" />
        <UpdatePaymentModal :event-id="store.eventId" :event-timezone="store.eventTimezone" ref="updatePaymentModal" />
        <PaymentDocumentPreviewModal ref="paymentDocumentModal" />
        <CompetitorStatsModal ref="competitorStatsModal" :event-id="store.eventId" />
        <StartingListsModal ref="startingListsModal" />
        <PredefinedNotificationModal ref="sendNotificationsModal" :event-id="store.eventId" :event-timezone="store.eventTimezone" />
        <AcademySelectModal ref="academySelectModal" />
        <EventRegistrationChangeSideEffectsReportModal ref="eventRegistrationChangeSideEffectsReportModal" />
        <AdminCategorySidebar
            ref="categorySidebar"
            :event-id="store.eventId"
            :event-timezone="store.eventTimezone"
            :show-roster-in-place="store.showOnlyCategory"
        />
    </section>
</template>

<script setup lang="ts">
    import { nextTick, onBeforeUnmount, onMounted, ref } from "vue";
    import history from "../Common/history";
    import {
        eventBus,
        OPEN_ACADEMY_FROM_COMPETITOR_EDIT,
        OPEN_CATEGORY_SIDEBAR,
        OPEN_COMPETITOR_STATS,
        OPEN_COPY_TO_CATEGORY_MODAL,
        OPEN_DELETE_COMPETITOR_MODAL,
        OPEN_EDIT_PAYMENT_MODAL,
        OPEN_EDIT_REGISTRATION_MODAL,
        OPEN_PAYMENT_DOCUMENT,
        OPEN_PAYMENT_SETTINGS_MODAL,
        OPEN_SIDE_EFFECTS_REPORT_MODAL,
        OPEN_STARTING_LISTS_MODAL,
        SEND_NOTIFICATIONS_MODAL,
    } from "./eventBus";
    import { DEFAULT_SORT_DIRECTION, DEFAULT_SORT_FIELD } from "./adminRegisteredCompetitorsApp";
    import RegisteredCompetitorsTopBar from "./RegisteredCompetitorsTopBar.vue";
    import RegisteredCompetitorsTable from "./RegisteredCompetitorsTable.vue";
    import RegisteredCompetitorsBottomBar from "./RegisteredCompetitorsBottomBar.vue";
    import RegisteredCompetitorCopyToCategoryModal from "./RegisteredCompetitorCopyToCategoryModal.vue";
    import RegisteredCompetitorDeleteModal from "./RegisteredCompetitorDeleteModal.vue";
    import EditRegisteredCompetitorModal from "./EditRegisteredCompetitorModal.vue";
    import PaymentsSettingsModal from "./Payments/PaymentsSettingsModal.vue";
    import UpdatePaymentModal from "./Payments/UpdatePaymentModal.vue";
    import PaymentDocumentPreviewModal from "./Payments/PaymentDocumentPreviewModal.vue";
    import CompetitorStatsModal from "./CompetitorStatsModal.vue";
    import StartingListsModal from "./StartingListsModal.vue";
    import { handleError } from "../Common/errorHandler";
    import PredefinedNotificationModal from "../PredefinedNotifications/PredefinedNotificationModal.vue";
    import type { PredefinedNotificationType } from "../Api/predefinedNotifications";
    import AcademySelectModal from "../Academies/AcademySelectModal.vue";
    import { translate } from "../Common/translations";
    import { useRegisteredCompetitorsStore } from "./registeredCompetitorsStore";
    import { CompetitorPaymentWithMetadata, PaymentConfirmationFilter, SortDirection, SortField } from "../Api/payments";
    import { ComponentExposed } from "vue-component-type-helpers";
    import { EventRegistrationSideEffectReport } from "../Common/types";
    import AdminCategorySidebar from "../Common/Fights/AdminCategorySidebar.vue";
    import EventRegistrationChangeSideEffectsReportModal from "../SideEffects/EventRegistrationChangeSideEffectsReportModal.vue";

    const store = useRegisteredCompetitorsStore();
    const historyInstance = history.getInstance(window);

    const props = defineProps<{
        eventId: number;
        eventTimezone: string;
        isFullOrganizer: boolean;
        currentUserId: number;
    }>();

    store.eventId = props.eventId;
    store.eventTimezone = props.eventTimezone;
    store.isFullOrganizer = props.isFullOrganizer;
    store.currentUserId = props.currentUserId;

    const initFromQueryParams = () => {
        store.query = historyInstance.getQueryParam("q") || "";
        store.deleted = historyInstance.getQueryParam("deleted") === "1";
        store.parameterId = parseInt(historyInstance.getQueryParam("parameterId") || "0");
        store.userId = parseInt(historyInstance.getQueryParam("userId") || "0");
        store.offset = parseInt(historyInstance.getQueryParam("offset") || "0");
        store.onlyMissingPayments = historyInstance.getQueryParam("onlyMissingPayments") === "1";
        store.onlyCompletePayments = historyInstance.getQueryParam("onlyCompletePayments") === "1";
        store.showDisqualified = historyInstance.getQueryParam("disqualified") === "1";

        const paymentConfirmationFilterParam = historyInstance.getQueryParam("paymentConfirmationFilter");
        store.paymentConfirmationFilter =
            paymentConfirmationFilterParam === "withConfirmation" || paymentConfirmationFilterParam === "withoutConfirmation"
                ? (paymentConfirmationFilterParam as PaymentConfirmationFilter)
                : "all";

        store.sortField = (historyInstance.getQueryParam("sortField") || DEFAULT_SORT_FIELD) as SortField;
        store.sortDirection = (historyInstance.getQueryParam("sortDirection") || DEFAULT_SORT_DIRECTION) as SortDirection;
    };

    const sendNotificationsModal = ref<InstanceType<typeof PredefinedNotificationModal> | null>(null);
    const copyToCategoryModal = ref<InstanceType<typeof RegisteredCompetitorCopyToCategoryModal> | null>(null);
    const deleteCompetitorModal = ref<InstanceType<typeof RegisteredCompetitorDeleteModal> | null>(null);
    const editRegisteredCompetitorModal = ref<ComponentExposed<typeof EditRegisteredCompetitorModal> | null>(null);
    const paymentSettingsModal = ref<InstanceType<typeof PaymentsSettingsModal> | null>(null);
    const updatePaymentModal = ref<ComponentExposed<typeof UpdatePaymentModal> | null>(null);
    const paymentDocumentModal = ref<InstanceType<typeof PaymentDocumentPreviewModal> | null>(null);
    const competitorStatsModal = ref<InstanceType<typeof CompetitorStatsModal> | null>(null);
    const startingListsModal = ref<InstanceType<typeof StartingListsModal> | null>(null);
    const academySelectModal = ref<InstanceType<typeof AcademySelectModal> | null>(null);
    const categorySidebar = ref<ComponentExposed<typeof AdminCategorySidebar> | null>(null);
    const eventRegistrationChangeSideEffectsReportModal = ref<ComponentExposed<
        typeof EventRegistrationChangeSideEffectsReportModal
    > | null>(null);

    eventBus.$on(SEND_NOTIFICATIONS_MODAL, (type: PredefinedNotificationType) => {
        sendNotificationsModal.value?.open(type);
    });
    eventBus.$on(OPEN_COPY_TO_CATEGORY_MODAL, (registeredCompetitor: CompetitorPaymentWithMetadata) => {
        copyToCategoryModal.value?.open(registeredCompetitor);
    });
    eventBus.$on(OPEN_DELETE_COMPETITOR_MODAL, (registeredCompetitor: CompetitorPaymentWithMetadata) => {
        deleteCompetitorModal.value?.open(registeredCompetitor);
    });
    eventBus.$on(OPEN_EDIT_REGISTRATION_MODAL, (registeredCompetitor: CompetitorPaymentWithMetadata) => {
        editRegisteredCompetitorModal.value?.open(registeredCompetitor);
    });
    eventBus.$on(OPEN_PAYMENT_SETTINGS_MODAL, () => {
        paymentSettingsModal.value?.open();
    });
    eventBus.$on(OPEN_EDIT_PAYMENT_MODAL, (competitor: CompetitorPaymentWithMetadata) => {
        updatePaymentModal.value?.open(competitor);
    });
    eventBus.$on(OPEN_PAYMENT_DOCUMENT, (url: string) => {
        paymentDocumentModal.value?.open(url);
    });
    eventBus.$on(OPEN_COMPETITOR_STATS, () => {
        competitorStatsModal.value?.open();
    });
    eventBus.$on(OPEN_STARTING_LISTS_MODAL, () => {
        startingListsModal.value?.open();
    });
    eventBus.$on(OPEN_ACADEMY_FROM_COMPETITOR_EDIT, (callbackForCompetitorEdit: any) => {
        academySelectModal.value?.open(callbackForCompetitorEdit);
    });
    eventBus.$on(OPEN_SIDE_EFFECTS_REPORT_MODAL, (report: EventRegistrationSideEffectReport) => {
        eventRegistrationChangeSideEffectsReportModal.value?.open(report);
    });

    const openCategorySidebar = (parameterId: number) => {
        categorySidebar.value?.open(parameterId);
    };
    eventBus.$on(OPEN_CATEGORY_SIDEBAR, openCategorySidebar);

    onBeforeUnmount(() => {
        eventBus.$off(OPEN_CATEGORY_SIDEBAR, openCategorySidebar);
    });

    // The dashboard checklist links here with ?openStartingListsModal=1. The parameter is always removed so a
    // refresh does not reopen the modal; only full organizers can use the modal.
    function initStartingLists() {
        const openRequested = historyInstance.getQueryParam("openStartingListsModal") === "1";
        historyInstance.setQueryParam("openStartingListsModal", null);

        if (!store.isFullOrganizer) {
            return;
        }

        store
            .loadStartingListsSharing()
            .then(() => {
                if (openRequested) {
                    nextTick(() => startingListsModal.value?.open());
                }
            })
            .catch(handleError);
    }

    onMounted(() => {
        initFromQueryParams();
        store.getRegisteredCompetitorsPage().catch(console.error);
        initStartingLists();
    });
</script>

<style scoped></style>
